import React, { useState } from 'react';
import { ShoppingBag, Sparkles, MessageCircle, Check, ArrowRight, Star, Heart } from 'lucide-react';
import { products, productCategories } from '../data/products';
import { salonInfo } from '../data/salonInfo';
import { useLanguage } from '../context/LanguageContext';

export default function ProductsSection({ onOpenBooking }) {
  const { lang } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  const handleOrderWhatsApp = (product) => {
    const prodName = product.name[lang] || product.name.nl;
    const msg = lang === 'nl'
      ? `Hallo Aura Hair, ik heb interesse in het product "${prodName}" (€${product.price.toFixed(2)}). Is dit op voorraad in de salon?`
      : lang === 'tr'
      ? `Merhaba Aura Hair, "${prodName}" (€${product.price.toFixed(2)}) ürünü ile ilgileniyorum. Salonda stokta mevcut mu?`
      : `Hello Aura Hair, I am interested in "${prodName}" (€${product.price.toFixed(2)}). Is it available in salon?`;
    
    const url = `https://wa.me/${salonInfo.whatsappRaw}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  return (
    <section id="producten" className="py-24 relative bg-stone-900 text-white overflow-hidden w-full border-t border-stone-800">
      
      {/* Background Glows */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 text-xs font-black uppercase tracking-widest mb-3 shadow-lg">
            <ShoppingBag className="w-3.5 h-3.5 text-amber-400" />
            <span>{lang === 'nl' ? 'SALON BOUTIQUE & VERZORGING' : lang === 'tr' ? 'SALON BUTİK & PROFESYONEL ÜRÜNLER' : 'SALON BOUTIQUE & CARE'}</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            {lang === 'nl' ? (
              <>Professionele <span className="text-amber-400 font-serif">Haarproducten & Pruiken</span></>
            ) : lang === 'tr' ? (
              <>Profesyonel <span className="text-amber-400 font-serif">Saç Ürünleri & Peruklar</span></>
            ) : (
              <>Professional <span className="text-amber-400 font-serif">Hair Products & Wigs</span></>
            )}
          </h2>

          <p className="mt-4 text-sm sm:text-base text-stone-300">
            {lang === 'nl'
              ? 'Verleng uw salonresultaat thuis met onze exclusieve selectie pruiken, haarextensions, sulfaatvrije shampoo\'s, maskers en stylingsets.'
              : lang === 'tr'
              ? 'Salondaki mükemmel görünümünüzü evde de koruyun: Doğal peruklar, kaynaklar, sülfatsız şampuanlar, maskeler ve berber serisi şekillendiriciler.'
              : 'Maintain salon-grade perfection at home with our curated line of premium wigs, hair extensions, sulfate-free shampoos, masks and styling essentials.'}
          </p>
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center justify-center flex-wrap gap-2.5 mb-12">
          {productCategories.map((cat) => {
            const isSelected = activeCategory === cat.id;
            const catName = cat.name[lang] || cat.name.nl;

            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 rounded-2xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black shadow-lg shadow-amber-500/30 scale-105'
                    : 'bg-stone-800 text-stone-300 hover:bg-stone-700 hover:text-white border border-stone-700'
                }`}
              >
                <span>{catName}</span>
              </button>
            );
          })}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => {
            const prodName = product.name[lang] || product.name.nl;
            const prodDesc = product.desc[lang] || product.desc.nl;

            return (
              <div
                key={product.id}
                className="rounded-3xl bg-stone-800/80 border border-stone-700 hover:border-amber-400/80 p-5 flex flex-col justify-between transition-all duration-300 group hover:scale-[1.02] shadow-xl hover:shadow-2xl hover:shadow-amber-500/10 relative overflow-hidden"
              >
                <div>
                  {/* Badge & Category */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-[10px] font-mono font-bold uppercase tracking-wider">
                      {product.badge}
                    </span>
                    <span className="text-amber-400 font-bold font-mono text-sm">
                      {product.isStartingPrice ? (lang === 'nl' ? 'Vanaf ' : lang === 'tr' ? 'Başlayan ' : 'From ') : ''}
                      €{product.price.toFixed(2)}
                    </span>
                  </div>

                  {/* Product Title */}
                  <h3 className="font-serif text-base sm:text-lg font-bold text-white mb-2 group-hover:text-amber-300 transition-colors line-clamp-2">
                    {prodName}
                  </h3>

                  {/* Product Description */}
                  <p className="text-xs text-stone-400 leading-relaxed font-normal mb-4 line-clamp-3">
                    {prodDesc}
                  </p>
                </div>

                {/* Bottom Actions */}
                <div className="pt-4 border-t border-stone-700/60 space-y-2">
                  <button
                    onClick={() => handleOrderWhatsApp(product)}
                    className="w-full py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-md shadow-emerald-600/20 active:scale-95 cursor-pointer"
                  >
                    <MessageCircle className="w-4 h-4 fill-white" />
                    <span>{lang === 'nl' ? 'WhatsApp Informatie / Bestellen' : lang === 'tr' ? 'WhatsApp ile Sipariş & Bilgi' : 'Order via WhatsApp'}</span>
                  </button>

                  <div className="flex items-center justify-between text-[11px] text-stone-400 px-1">
                    <span className="flex items-center gap-1 text-emerald-400">
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                      <span>{lang === 'nl' ? 'Direct in salon verkrijgbaar' : lang === 'tr' ? 'Salonda hemen teslim' : 'In stock at salon'}</span>
                    </span>
                    <span className="font-mono text-stone-500">Samuel Esmeijerplein</span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {/* Bottom Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-stone-800 via-amber-950/40 to-stone-800 border border-amber-500/40 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-2xl">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="font-serif text-xl sm:text-2xl font-black text-white">
              {lang === 'nl' ? 'Persoonlijk Productadvies Nodig?' : lang === 'tr' ? 'Hangi Ürünün Size Uygun Olduğundan Emin Değil Misiniz?' : 'Need Personalized Product Advice?'}
            </h4>
            <p className="text-xs sm:text-sm text-stone-300">
              {lang === 'nl'
                ? 'Onze ervaren stylisten analyseren uw haar en hoofdhuid in de salon voor een advies op maat.'
                : lang === 'tr'
                ? 'Uzman kuaförlerimiz saç tipinizi inceleyerek size en uygun şampuan, bakım maskesi ve serumu belirlesin.'
                : 'Our master stylists assess your hair and scalp in the salon to recommend the ideal regimen.'}
            </p>
          </div>

          <button
            onClick={onOpenBooking}
            className="btn-gold-sheen px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-slate-950 font-black text-xs sm:text-sm uppercase tracking-wider shadow-xl shadow-amber-500/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shrink-0 cursor-pointer"
          >
            <span>{lang === 'nl' ? 'Afspraak Inplannen' : lang === 'tr' ? 'Randevu Oluştur' : 'Book Appointment'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </div>
    </section>
  );
}
